/**
 * SidebarNav 编译器
 *
 * 解决 Unpoly 缓存与导航上下文不一致的跳动问题：
 * - Unpoly 缓存按 URL 做 key，但 sidebar 高亮状态取决于「从哪个页面进入」
 * - 当缓存命中时，sidebar HTML 中的 activeNav 可能是旧值
 * - 此编译器在 up-hungry 替换 sidebar 后同步修正高亮状态（绘制前执行，无闪烁）
 *
 * 状态存储：sessionStorage('lastNav')，同步读写、无竞态、标签页隔离
 */

import { isMainRoute, isNavActive } from "utils/nav";

export class SidebarNav {
  private static readonly STORAGE_KEY = "lastNav";

  /**
   * 获取所有的主导航 ID
   */
  private static getNavIds(sidebar: Element): string[] {
    const links = sidebar.querySelectorAll<HTMLElement>(
      '[data-type="nav"] [data-nav-id]',
    );
    return Array.from(links)
      .map((el) => el.getAttribute("data-nav-id")!)
      .filter(Boolean);
  }

  /**
   * 根据当前路径计算应该高亮的导航项
   * 如果当前在主路径，直接使用；否则从 sessionStorage 读取记忆值
   */
  private static resolveActiveNav(navIds: string[]): string {
    const fullPath = location.pathname;
    // 剥离语言前缀（例如 /zh/favorites -> /favorites, /zh -> /）
    const relativePath = fullPath.replace(/^\/(zh|en)(\/|$)/, "/");

    if (isMainRoute(relativePath, navIds)) {
      // 存储匹配到的基准 ID（例如在 /favorites/page/2 时存储 /favorites）
      const matchedId =
        navIds.find((id) => isNavActive(relativePath, id)) || "/";
      sessionStorage.setItem(SidebarNav.STORAGE_KEY, matchedId);
      return matchedId;
    }

    return sessionStorage.getItem(SidebarNav.STORAGE_KEY) || "/";
  }

  /**
   * 更新 sidebar 链接的高亮状态
   * 使用与服务端模板一致的 class 切换逻辑：up-current / text-muted
   */
  private static updateActiveState(sidebar: Element): void {
    const navIds = SidebarNav.getNavIds(sidebar);
    const activeNav = SidebarNav.resolveActiveNav(navIds);

    const navItems = sidebar.querySelectorAll<HTMLElement>(
      '[data-type="nav"] [data-nav-id]',
    );

    navItems.forEach((item) => {
      const navId = item.getAttribute("data-nav-id");
      if (!navId) return;

      const isActive = isNavActive(activeNav, navId);

      item.classList.toggle("up-current", isActive);
      item.classList.toggle("text-muted", !isActive);
    });
  }

  /**
   * 注册 Unpoly 编译器
   * 每次 sidebar 被 up-hungry 插入/替换时自动执行
   */
  static compiler(): void {
    up.compiler("#sidebar-nav", (element) => {
      SidebarNav.updateActiveState(element);
    });
  }
}
